import { getThreadScorerIdsForAgent } from '@fruitful/llm-service-shared';
import type { AgentSimulationScorerResult } from '@fruitful/types';

import { getThreadScorerResult } from './threadMetadataUtils';

/** Thread scorer results keyed by scorer id (same shape as `metadata.scorers` entries). */
export type QAThreadScorers = Record<string, { score: number }>;

/**
 * Read thread-level scorers from metadata, filtered to the agent's registered
 * full-thread scorers (today: the judge scorer only).
 */
export const getThreadScorersForAgent = (
    metadata: unknown,
    agentId: string,
): QAThreadScorers | null => {
    const entries: Array<[string, { score: number }]> = [];

    for (const scorerId of getThreadScorerIdsForAgent(agentId)) {
        const result = getThreadScorerResult(metadata, scorerId);
        if (result != null) {
            entries.push([scorerId, { score: result.score }]);
        }
    }

    return entries.length > 0 ? Object.fromEntries(entries) : null;
};

/**
 * Convert a simulation `scored` event into QA thread scorers, filtered to the
 * agent's registered full-thread scorers.
 */
export const threadScorersFromSimulationScores = (
    scores: AgentSimulationScorerResult[],
    agentId: string,
): QAThreadScorers | null => {
    const allowedScorerIds = new Set(getThreadScorerIdsForAgent(agentId));
    const entries: Array<[string, { score: number }]> = scores
        .filter((score) => allowedScorerIds.has(score.scorerId))
        .map((score) => [score.scorerId, { score: score.score }]);

    return entries.length > 0 ? Object.fromEntries(entries) : null;
};

/** Score for a specific scorer id from a QA scorers map. */
export const getThreadScorerScore = (
    scorers: QAThreadScorers | null | undefined,
    scorerId: string,
): number | null => {
    const score = scorers?.[scorerId]?.score;
    return typeof score === 'number' ? score : null;
};
